import AddRounded from '@mui/icons-material/AddRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { BaseModal } from '@src/common/components/BaseModal';
import { Button } from '@src/common/components/button/Button';
import { FilterableTable } from '@src/common/components/FilterableTable';
import { IconButton } from '@src/common/components/IconButton';
import { LoadingSpinner } from '@src/common/components/LoadingSpinner';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { ClientsNavigation } from '@src/routing/ClientsNavigation';
import { Navigation } from '@src/routing/Navigation';
import { useDeleteTrailer } from '@src/trailer/hooks/useDeleteTrailer';
import { t } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTrailers } from './hooks/useTrailers';

type Props = {
  clientId: string;
};

export const TrailersList = ({ clientId }: Props) => {
  const { t } = useTranslation();
  const {
    trailers: { trailers },
    isLoading,
  } = useTrailers(clientId);
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center">
        <LoadingSpinner />
      </Stack>
    );
  }

  const columns: GridColDef[] = [
    { field: 'unitNumber', headerName: t('unitNumber'), flex: 1 },
    { field: 'year', headerName: t('year'), flex: 1 },
    { field: 'make', headerName: t('make'), flex: 1 },
    {
      field: 'licensePlate',
      headerName: t('fields.trailerLicensePlate'),
      flex: 1,
    },
    {
      field: 'Actions',
      headerName: '',
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      hideable: false,
      renderCell: (params) => {
        return (
          <>
            <IconButton
              onClick={() =>
                navigate(
                  ClientsNavigation.trailers.edit.url(clientId, params.row.id),
                )
              }
              title={t('editTrailer')}
            >
              <Edit />
            </IconButton>
            <DeleteTrailerButton
              clientId={clientId}
              trailerId={params.row.id}
            />
          </>
        );
      },
    },
  ];

  const rows: Record<string, string>[] = trailers.map((trailer) => {
    return {
      id: trailer.id,
      unitNumber: trailer.unitNumber,
      year: trailer.year.toString(),
      make: trailer.make,
      licensePlate: trailer.licensePlateNumber,
    };
  });

  const isEmptyList = trailers.length === 0;

  return (
    <>
      <Stack alignItems="flex-end" className={addTrailerButtonContainer}>
        <Button
          className={addTrailerButton}
          variant="contained"
          onClick={() =>
            navigate(Navigation.clients.trailers.add.url(clientId))
          }
          startIcon={<AddRounded />}
        >
          {t('addTrailer')}
        </Button>
      </Stack>
      {isEmptyList && (
        <Typography className={emptyList} variant="body2">
          {t('emptyTrailerList')}
        </Typography>
      )}
      {!isEmptyList && (
        <FilterableTable
          ariaLabel="trailers-list"
          columns={columns}
          rows={rows}
          paginationAndSortMode="client"
        />
      )}
    </>
  );
};

const DeleteTrailerButton = ({
  clientId,
  trailerId,
}: {
  clientId: string;
  trailerId: string;
}) => {
  const [open, setOpen] = useState(false);
  const { showErrorToast, showSuccessToast } = useToast();

  const { deleteTrailer, isLoading } = useDeleteTrailer({
    clientId,
    trailerId,
    onSuccess: () => {
      setOpen(false);
      showSuccessToast(t('deleteTrailer.success'));
    },
    onError: () => {
      showErrorToast(t('deleteTrailer.error'));
    },
  });

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        title={t('deleteTrailer.title')}
      >
        <DeleteIcon />
      </IconButton>
      <BaseModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('deleteTrailer.title')}
      >
        <Stack gap={theme.spacing['3']}>
          <Typography>{t('deleteTrailer.message')}</Typography>
          <CloseConfirmModalFooter
            onClose={() => setOpen(false)}
            confirm={() => deleteTrailer()}
            closeLabel={t('cancel')}
            confirmLabel={t('delete')}
            confirmLoading={isLoading}
            confirmDisabled={isLoading}
          />
        </Stack>
      </BaseModal>
    </>
  );
};

const emptyList = css({
  marginLeft: theme.spacing['4'],
});

const addTrailerButtonContainer = css({
  marginBottom: theme.spacing['3'],
  marginTop: theme.spacing['3'],
});

const addTrailerButton = css({
  backgroundColor: theme.palette.primary.primary,
  borderRadius: '8px',
});
